/**
 * The truncation band (mission 6 WP08 T045; FR-013, FR-017; ANA.12, P.8;
 * research §1.18; DM-01M39PR43C8WYP2S7T8VYTYTZE, DM-01M39TVGN8PD0659S6ZFE3P726).
 *
 * The views list at most the first 500 findings of each rule, so each
 * truncated (rule, severity) segment is named here, with its count, the rows
 * listed (`in_window`, 0 included) and the last listed line, worded by register
 * 8 (which takes `inWindow`, owner decision 2026-09-26) and in mono. The band offers the complete list: Export CSV. It is an info
 * band (`role="status"`), never a toast, and renders above the tab panels so
 * every tab states it. Beyond five segments the rest sit behind a disclosure.
 * Nothing renders when nothing is truncated.
 */
import { Fragment, useId, useState, type ReactNode } from 'react'
import { ChevronDown } from 'lucide-react'

import { Band } from '@/components/trellis/Band'
import { ExportButton } from '@/components/trellis/ExportButton'
import { cn } from '@/components/ui/cn'
import type { TruncatedRule } from '@/lib/api/findings'
import { Mono, count } from '@/lib/format'
import { PROVISIONAL_COPY } from '@/lib/provisional-copy'

const VISIBLE = 5

// Placeholders passed to the register's render, then swapped for mono nodes,
// so the sentence stays the register's own words (FR-025) and the values mono.
const SLOT = {
  segment: '\u0000segment\u0000',
  count: '\u0000count\u0000',
  inWindow: '\u0000inWindow\u0000',
  listedThrough: '\u0000line\u0000',
}

function sentence(t: TruncatedRule): ReactNode[] {
  const text = PROVISIONAL_COPY.truncationBand.render(SLOT)
  const nodes: Record<string, ReactNode> = {
    segment: (
      <>
        <Mono>{t.rule_id}</Mono> {t.severity}
      </>
    ),
    count: <Mono>{count(t.count)}</Mono>,
    inWindow: <Mono>{count(t.in_window)}</Mono>,
    line: <Mono>{count(t.listed_through_line)}</Mono>,
  }
  return text.split('\u0000').map((part, i) => <Fragment key={i}>{i % 2 === 1 ? nodes[part] : part}</Fragment>)
}

function Segment({ t }: { t: TruncatedRule }) {
  return <li>{sentence(t)}</li>
}

export function TruncationBand({ truncated, runId }: { truncated: TruncatedRule[]; runId: string }) {
  const [open, setOpen] = useState(false)
  const restId = useId()
  if (truncated.length === 0) return null
  const first = truncated.slice(0, VISIBLE)
  const rest = truncated.slice(VISIBLE)
  return (
    <Band kind="info" action={<ExportButton runId={runId} />}>
      <ul className="flex flex-col gap-1">
        {first.map((t) => (
          <Segment key={`${t.rule_id}|${t.severity}`} t={t} />
        ))}
      </ul>
      {rest.length > 0 ? (
        <>
          <ul id={restId} hidden={!open} className="mt-1 flex flex-col gap-1">
            {rest.map((t) => (
              <Segment key={`${t.rule_id}|${t.severity}`} t={t} />
            ))}
          </ul>
          <button
            type="button"
            aria-expanded={open}
            aria-controls={restId}
            onClick={() => setOpen(!open)}
            className="mt-1 inline-flex items-center gap-1 rounded-sm text-body-sm text-[var(--color-link)] hover:text-[var(--color-link-hover)] focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]"
          >
            <Mono>{count(rest.length)}</Mono> more
            <ChevronDown aria-hidden className={cn('size-4 transition-transform duration-[var(--duration-fast)]', open && 'rotate-180')} />
          </button>
        </>
      ) : null}
    </Band>
  )
}
